import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING,
} from '../GuideKit';

/* Trading → Model Management → Rebalance Model(s) / Create New Model(s) /
 * Edit Existing Model(s).
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version active 2026-10-04): screens "Rebalance Model(s)" and "Rebalance
 * Model (Client Accounts)" for a rebalance; "Create New Model(s)" and "Edit
 * Existing Model(s)" for model work, each behind the advisor's ADP Level II
 * check ("Not Approved to Create New Model(s)" otherwise). Each sub-category
 * is written against the screen named for it. The Create and Edit screens are
 * mostly a custom Model Works component (c:prosperitySecurities) whose
 * contents are not in the flow metadata, so the guide does not describe its
 * inner fields. Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Model Management',
    subCategory: 'Rebalance Model(s), Create New Model(s) or Edit Existing Model(s)',
    subject: subjectRow('Rebalance model - DFA TM 100/0', { noAccount: true }),
    lookups: [
      ['Household', 'Required on every Trading case, model requests included. On a rebalance it is stamped on the case.'],
      ['Advisor', 'Model Management only. The models you can pick come from this advisor’s office, the case is filed under this advisor and office, and for Create or Edit this is the advisor whose ADP approval is checked.'],
    ],
    note: (
      <>
        <strong>The three sub-categories lead to different screens</strong> - a rebalance picks a model and
        client accounts; Create and Edit open the Model Works panel. The reference below is split the same way.
      </>
    ),
  }),
  {
    title: 'Rebalance Model(s): pick the model and describe the rebalance',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>Rebalance Model(s)</strong> screen lists the advisor&rsquo;s office models in a table
          labelled <em>Select Model (Rebalance Model)</em>, with Model Name, Management Style and Office / Team.
          Pick exactly one.
        </p>
        <p style={{ margin: 0 }}>
          Then fill in <em>Please provide as much information as possible for the Trading Team</em>. It is
          required and becomes the case description Trading reads first. Acknowledge the notice and click Next.
        </p>
      </>
    ),
  },
  {
    title: 'Rebalance Model(s): tick the client accounts',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>Rebalance Model (Client Accounts)</strong> screen lists the client accounts currently in
          the model you picked - across the office, not just the household on the header, which is why it has a
          Household column. Tick every account the rebalance should cover.
        </p>
        <p style={{ margin: 0 }}>
          The screen reminds you: <em>For Non-Qualified Accounts - please let us know the Capital Gains threshold
          for each account.</em> This screen has no box for it, so put the thresholds in the details box on the
          previous screen - use Back if you need to add them. Acknowledge the notice again and click Next.
        </p>
      </>
    ),
  },
  {
    title: 'Create New Model(s) or Edit Existing Model(s): complete the Model Works panel',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Before these screens open, FSC checks that the <strong>advisor on the header</strong> is ADP Level II
          (Model Works) approved. If not, you see <strong>Not Approved to Create New Model(s)</strong> - see the
          warning below the steps.
        </p>
        <p style={{ margin: 0 }}>
          If the advisor is approved, the screen is a single required Model Works panel: on{' '}
          <strong>Create New Model(s)</strong> it is titled <em>Model Works Information</em>; on{' '}
          <strong>Edit Existing Model(s)</strong> it lists the office&rsquo;s Model Works models to choose from.
          Complete and save the panel. There is no separate details box on these screens.
        </p>
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice - it governs timing and what the teams can and cannot do. Select{' '}
        <em>I have read, understand, and agree to the above information</em> under Acknowledgement and click
        Next. FSC creates the case, shares it with your office and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

// Every question, grouped by the screen it appears on.
const QUESTIONS = [
  {
    screen: 'Rebalance Model(s)',
    q: 'Select Model (Rebalance Model)',
    when: 'Rebalance Model(s) - always, first on the screen',
    note: 'A table with Model Name, Management Style and Office / Team. Pick exactly one row. It lists active models for the advisor’s office only; Separately Managed Accounts (SMA), Advisor Select Portfolios (ASP) and Tax-Smart Advisor Directed (TSAD) management styles are left out.',
  },
  {
    q: 'Please provide as much information as possible for the Trading Team',
    when: 'Rebalance Model(s) - always',
    note: 'Required. This becomes the case description. Say what the rebalance should achieve, any timing, and the Capital Gains threshold for each non-qualified account you will tick on the next screen.',
  },
  {
    q: 'Acknowledgement',
    when: 'Rebalance Model(s) - under the notice',
    options: ['I have read, understand, and agree to the above information'],
  },
  {
    screen: 'Rebalance Model (Client Accounts)',
    q: 'Rebalance Model (Client Accounts)',
    when: 'Rebalance Model(s) - second screen',
    note: 'A table with Account, Household, Account Details, Model, Management Style, Cash Balance and Balance. Tick as many rows as apply. It lists active accounts in the chosen model with a balance over $100.',
  },
  {
    q: 'Acknowledgement',
    when: 'Rebalance Model(s) - second screen, under the notice',
    options: ['I have read, understand, and agree to the above information'],
  },
  {
    screen: 'Create New Model(s)',
    q: 'Model Works Information',
    when: 'Create New Model(s) - only if the advisor is ADP Level II approved',
    note: 'A required Model Works panel where you set up the new model. Save it before you click Next.',
  },
  {
    q: 'Acknowledgement',
    when: 'Create New Model(s) - under the notice',
    options: ['I have read, understand, and agree to the above information'],
  },
  {
    screen: 'Edit Existing Model(s)',
    q: 'Model Works panel (existing models)',
    when: 'Edit Existing Model(s) - only if the advisor is ADP Level II approved',
    note: 'A required panel listing the office’s Model Works models. The screen says: "Please note - Only Model Works Models are rendered in the list below. If you do not see a Model you believe should be here, please reach out to the Trading Team (tradingops@prosperitycapitaladvisors.com)." Make your changes and save before you click Next.',
  },
  {
    q: 'Acknowledgement',
    when: 'Edit Existing Model(s) - under the notice',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Model Management'),
  {
    h: 'I got a "Not Approved to Create New Model(s)" message',
    p: [
      'Create New Model(s) and Edit Existing Model(s) check the advisor chosen on the header, not you. The message means that advisor is not yet ADP (ModelWorks) approved - "All Advisors must go through this training before they may create and manage Model Works Models."',
      'Stop there and close the form - do not click Next. Once the advisor has completed the ADP training, open a new case. Questions go to tradingops@prosperitycapitaladvisors.com.',
    ],
  },
  {
    h: "The model I want isn't in the list",
    p: [
      'Every model list is limited to the office of the advisor on the header - if you picked the wrong advisor, cancel and start again.',
      'For a rebalance, only active models are listed, and SMA, ASP and TSAD management styles are left out. For Edit Existing Model(s), only Model Works models are listed. If a model you expect is missing, email tradingops@prosperitycapitaladvisors.com.',
    ],
  },
  {
    h: 'The client accounts table shows other households, or is missing an account',
    p: [
      'The table lists every active account in the chosen model with a balance over $100, whichever household it belongs to - check the Household column and tick only the accounts you mean. An account at $100 or less, or an inactive one, will not appear.',
    ],
  },
];

export default function ModelManagement() {
  return (
    <GuidePage
      title="Model Management Requests"
      icon="diagram-3"
      path="Trading → Model Management → Rebalance, Create or Edit Model(s)"
      minutes={5}
      intro="How to ask the Prosperity Trading team to rebalance a model, or to create or edit a Model Works model - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. What you need depends on the sub-category.</Para>
        <Bullets items={[
          <><strong>The advisor</strong> the model belongs to. The models you can pick come from that advisor&rsquo;s office.</>,
          <><strong>A household</strong> to put on the header - the form requires one for every Trading case.</>,
          <><strong>For a rebalance:</strong> the model name, the client accounts to include, and the Capital Gains threshold for each non-qualified account.</>,
          <><strong>For Create or Edit:</strong> the model details to enter in the Model Works panel, and confirmation that the advisor is ADP Level II (Model Works) approved.</>,
        ]} />
        <Callout tone="warn">
          Create New Model(s) and Edit Existing Model(s) are only open to advisors who have completed the ADP
          (ModelWorks) training. The check is made against the advisor on the case, so it does not matter who
          fills in the form. Rebalance Model(s) has no such check.
        </Callout>
        <Callout>
          If a non-qualified account has no Capital Gains threshold, the form warns this <em>could result in
          adverse tax consequences for the client</em>. Agree the thresholds with the client before you submit.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare, or to work out why a screen you expected has not shown up. Every item is required once it appears.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="trading" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe model construction, the ADP
        training, or the suitability of a model or rebalance for any client.
      </Footnote>
    </GuidePage>
  );
}
